import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  AvatarGroup,
  Avatar,
  Chip,
  Fab,
  CardHeader,
  Divider,
  CardContent,
  TextField,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import HelpIcon from '@mui/icons-material/Help';
import useWidget from 'src/components/app/useWidget';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import CodeIcon from '@mui/icons-material/Code';
import ConfirmDialog from 'src/components/settings/menuItems/ConfirmDialog';
import UpgradePlanDialog from 'src/components/app/UpgradePlanDialog';
import Error from 'src/components/app/Error';
import LoadingProgress from 'src/components/app/LoadingProgress';
import WidgetsDialog from './WidgetsDialog';
import WidgetCodeDialog from './WidgetCodeDialog';
import { Cancel, ContentCopy, List, Search, SwapHoriz, Warning } from '@mui/icons-material';
import WidgetCopyDialog from './WidgetCopyDialog';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import CountProgress from '../app/CountProgress';
import $api from '../http';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useAppConext } from '../context/AppContext';
import WidgetTransferDialog from './WidgetTransferDialog';
import useWorkspace from '../app/useWorkspace';

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[3],
    fontSize: 11,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.background.paper,
  },
}));

let searchTimeout = null;

const WidgetsList = (props) => {
  const { t } = useTranslation();
  const [loadError, setLoadError] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [items, setItems] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [currentWidgetUid, setCurrentWidgetUid] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);

  const { configUrl, showFab, showBreadcrumbs, agency } = props;
  const { enqueueSnackbar } = useSnackbar();
  const widget = useWidget();
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const { user, isFree, isPro, getFeatureValue } = useAppConext();

  const loadContent = (search, isSearch = false) => {
    if (isSearch) {
      setSearchLoading(true);
    }
    $api.get(`${configUrl}?s=${search}`)
      .catch((e) => {
        setLoadError(e);
        return null;
      })
      .then((result) => {
        if (result && result.data) {
          setItems(result.data);
        }
      })
      .finally(() => {
        setIsLoaded(true);
        setSearchLoading(false);
      })
  };

  const handleSearchTermChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      loadContent(val, true);
    }, 500);
  };

  const clearSearchTerm = () => {
    setSearchTerm('');
    loadContent('', true);
  };

  const listener = () => {
    loadContent(searchTerm);
  };

  const workspaceListener = () => {
    loadContent(searchTerm);
  };

  useEffect(() => {
    widget.subscribe(listener);
    workspace.subscribe(workspaceListener);
    return () => {
      widget.unsubscribe(listener);
      workspace.unsubscribe(workspaceListener);
    };
  }, [listener]);

  useEffect(() => {
    loadContent(searchTerm);
    return () => {
      widget.unsubscribe(listener);
      workspace.unsubscribe(workspaceListener);
    };
  }, []);

  const handleClickAddItem = () => {
    setCurrentItem({
      id: null,
      type: 1,
      message: '',
      active: 1
    });
    if (getFeatureValue('widgets.limit') <= items?.length) {
      setUpgradeDialogOpen(true);
    } else {
      setDialogOpen(true);
    }
  };

  const handleClickEditItem = (item) => {
    setCurrentItem(item);
    setDialogOpen(true);
  };

  const handleClickCopyItem = (item) => {
    if (getFeatureValue('widgets.limit') <= items?.length) {
      setUpgradeDialogOpen(true);
    } else {
      setCurrentItem(item);
      setCurrentWidgetUid(item.uid);
      setCopyDialogOpen(true);
    }
  };

  const handleClickTransferItem = (item) => {
    setCurrentItem(item);
    setTransferDialogOpen(true);
  };

  const handleClickCodeItem = (item) => {
    setCurrentItem(item);
    setCodeDialogOpen(true);
  };

  const handleClickManageChannels = (item) => {
    widget.setWidget(item.uid);
    navigate('/settings/channels', { replace: true });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleCloseCodeDialog = () => {
    setCodeDialogOpen(false);
  };

  const handleSaveConfirmDialog = (data) => {
    widget.notify();
    enqueueSnackbar(t('Widget is removed'), {
        variant: 'success'
    });
    loadContent(searchTerm);
  };

  const handleRemoveItem = (item) => {
    setCurrentItem(item);
    setConfirmDialogOpen(true);
  };

  const handleSaveDialog = () => {
    widget.notify();
    enqueueSnackbar(t('Widget is saved'), {
        variant: 'success'
    });
    loadContent(searchTerm);
  };

  if (!isLoaded) {
    return (<LoadingProgress open />);
  }
  if (loadError && Object.keys(loadError).length !== 0) {
    return (
      <Error error={loadError} />
    );
  }
  const getFirstDomain = (domains) => {
    const [firstElement] = domains;
    return firstElement || '';
  };

  return (
    <Card>
      <CardHeader
        title={(
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: '500', flex: 1 }}>
              {agency ? t('Agency widgets') : t('Your widgets')}
            </Typography>
            <Box sx={{ ml: 'auto', mr: 2, flex: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder={agency ? t('Widget name, website URL, or user email address') : t('Widget name or website URL')}
                onChange={handleSearchTermChange}
                value={searchTerm}
                sx={{ 
                  '& .MuiInputBase-root': {
                    pr: 0.4
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchLoading ? <CircularProgress size={24} /> : <Search sx={{ color: 'text.secondary' }} />}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton sx={{
                        display: searchTerm === '' ? 'none' : 'inline-flex'
                      }} onClick={clearSearchTerm} size="small">
                        <Cancel sx={{ color: 'text.secondary', opacity: 0.8 }} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </Box>
        )}
        sx={{
          '& .MuiCardHeader-action': {
            my: 'auto'
          }
        }}
        action={(
          <Box
              sx={{
                p: 0,
                display: 'flex',
              }}
            >
              <>
                {((getFeatureValue('widgets.limit') !== 'inf') && !agency) && (
                  <Box sx={{ minWidth: {
                      xs: 120,
                      sm: 160,
                      md: 200
                  }, display: 'flex', ml: 'auto' }}>
                    <CountProgress total={getFeatureValue('widgets.limit')} count={items.length} />
                  </Box>
                )}
                {(!showFab && !agency && ((getFeatureValue('widgets.limit') === 'inf') || (user?.permissions?.upgrade === true) || (getFeatureValue('widgets.limit') > items.length))) && (
                  <Box sx={{ mr: 1, ml: (isFree() || isPro()) ? 1 : 'auto', my: 'auto' }}>
                      <Button startIcon={<PlaylistAddIcon />} onClick={handleClickAddItem} variant="contained">
                        {t('Add widget')}
                      </Button>
                  </Box>
                )}
              </>
              {(user?.permissions?.docs) && ( 
                <Box sx={{ flexGrow: ((isFree() || isPro() || !showFab)) ? 0 : 1,  justifyContent: 'flex-end', display: 'flex' }}>
                  <Tooltip title={t('Help')}>
                    <IconButton sx={{ my: 'auto' }} component="a" href="https://docs.anychat.one/fundamentals/widgets" target="_blank">
                      <HelpIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
        )}
      />
      <Divider />
      <CardContent sx={{ p: '0 !important' }}>
      {(items.length !== 0) && (
        <>
          {showBreadcrumbs && (
            <Box>
              {(showFab && ((getFeatureValue('widgets.limit') === 'inf') || (user?.permissions?.upgrade === true) || (getFeatureValue('widgets.limit') > items.length))) && (
                <Tooltip placement="left" arrow title={t('Add widget')}>
                  <Fab sx={{
                      position: 'fixed',
                      bottom: 20,
                      right: 20,
                  }} color="primary" aria-label="Add widget" onClick={handleClickAddItem}>
                      <PlaylistAddIcon />
                  </Fab>
                </Tooltip>
              )}
            </Box>
          )}
          <Card>
            <PerfectScrollbar>
              <Box sx={{ minWidth: 500 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t('Widget name')}
                      </TableCell>
                      <TableCell>
                        {t('Domains')}
                      </TableCell>
                      <TableCell>
                        {t('Contact channels')}
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item) => (
                      <TableRow
                        hover
                        key={item.id}
                      >
                        <TableCell>
                          <Typography>
                            {item.name}
                          </Typography>
                          {(item.email && !item.isMy) && (
                            <Typography variant="body2" color="text.secondary">
                              {item.email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            color="textPrimary"
                          >
                            {getFirstDomain(item.realDomains)}
                            {(item.realDomains.length === 0) && (
                              <LightTooltip arrow title={
                                <>
                                  <Typography color="error" mb={1} variant="body2">
                                    {t('Domains are not set for this widget')}
                                  </Typography>
                                  <Typography color="text.primary" sx={{ fontWeight: 500 }} variant="body2">
                                    {t("Why it's important?")}
                                  </Typography>
                                  <Typography color="text.primary" variant="body2">
                                    {t('Domain whitelisting is a method of ensuring that no one but you is using your widget.')}
                                    {t('Also, we use cross-domain requests so that those requests will be allowed for whitelisted domains only.')}
                                  </Typography>
                                </>
                              }>
                                <Warning color="error" />
                              </LightTooltip>
                            )}
                            {(item.realDomains.length -1 > 0) && (
                              <Tooltip
                                title={(
                                  <>
                                    {item.realDomains.map((dom) => (
                                      <Typography
                                        key={dom}
                                        variant="body2"
                                        sx={{
                                          '& a': {
                                            color: 'white'
                                          },
                                          '& a:hover': {
                                            textDecoration: 'underline'
                                          }
                                        }}
                                      >
                                        <a href={dom} rel="noreferrer" target="_blank">{dom}</a>
                                      </Typography>
                                    ))}
                                  </>
                                )}
                              >
                                <Typography
                                  color="primary"
                                  component="span"
                                >
                                  &nbsp;+
                                  {item.realDomains.length - 1}
                                </Typography>
                              </Tooltip>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {(item.items.length > 0) ? (
                            <AvatarGroup sx={{
                              justifyContent: 'left',
                              '& .MuiAvatar-root': {
                                width: 32,
                                height: 32,
                                fontSize: '1rem'
                              }
                            }} max={4}>
                              {(item.items.map((i) => (
                                <Avatar key={i.id} sx={{ bgcolor: i.color, width: 32, height: 32 }}>
                                  <Box
                                    sx={{
                                      width: i.params.icon_type === 2 ? '100%' : 20,
                                      height: i.params.icon_type === 2 ? '100%' : 20,
                                      m: 'auto',
                                      '& svg': {
                                        display: 'block',
                                        width: 20,
                                        height: 20,
                                        m: 'auto'
                                      },
                                      '& img': {
                                        display: 'block',
                                        width: '100%',
                                        height: '100%',
                                        m: 'auto'
                                      },
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: i.icon_svg
                                    }}
                                  />
                                </Avatar>
                              )))}
                            </AvatarGroup>
                          ) : (
                            <Chip onClick={() => handleClickManageChannels(item)} color="warning" label="No channels set" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('Manage channels')}>
                            <IconButton aria-label="edit" onClick={() => handleClickManageChannels(item)} size="large">
                              <List fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('Widget script code')}>
                            <IconButton aria-label="edit" onClick={() => handleClickCodeItem(item)} size="large">
                              <CodeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('Widget settings')}>
                            <IconButton aria-label="edit" onClick={() => handleClickEditItem(item)} size="large">
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {((user?.permissions?.upgrade === true) || (getFeatureValue('widgets.limit') > items.length)) && (
                            <Tooltip title={t('Duplicate widget')}>
                              <IconButton aria-label="edit" onClick={() => handleClickCopyItem(item)} size="large">
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(getFeatureValue('agency.enabled') === true) && (
                            <Tooltip title={t('Transfer widget')}>
                              <IconButton aria-label="edit" onClick={() => handleClickTransferItem(item)} size="large">
                                <SwapHoriz fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={
                            (widget.getWidget() === item.uid || items.length === 1) ? t("Current widget can't be deleted") :
                            (item.canDelete === false) ? t("It's the only widget in account. You can't delete the last widget. Please create another widget to delete this one.")
                            : t('Delete widget')
                          }>
                            <Box sx={{ display: 'inline-block' }}>
                              <IconButton
                                aria-label="delete"
                                onClick={() => handleRemoveItem(item)}
                                disabled={widget.getWidget() === item.uid || item.canDelete === false}
                                size="large"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </PerfectScrollbar>
          </Card>
        </>
      )}
      {(items.length === 0) && (
        <>
          <Box sx={{ textAlign: 'center', pt: 4, pb: 4 }}>
            <Typography
              align="center"
              color="textPrimary"
              variant="h1"
            >
              {t('No widgets')}
            </Typography>
            <Typography
              align="center"
              color="textPrimary"
              variant="subtitle2"
            >
              {(searchTerm !== '' && searchTerm !== null) ? (
                t('No widget matches the request.')
              ) : (
                t('You have no widgets yet.')
              )}
            </Typography>
          </Box>
        </>
      )}
      <WidgetsDialog open={dialogOpen} saveUrl="/widgets" onSave={handleSaveDialog} onClose={handleCloseDialog} currentItem={currentItem} />
      <WidgetCodeDialog open={codeDialogOpen} onClose={handleCloseCodeDialog} currentItem={currentItem} />
      <UpgradePlanDialog requiredPlan="Multisite" open={upgradeDialogOpen} title={t('Upgrade to Multisite')} onClose={() => setUpgradeDialogOpen(false)} />
      <WidgetCopyDialog
        uid={currentWidgetUid}
        open={copyDialogOpen}
        onClose={() => setCopyDialogOpen(false)}
        currentItem={currentItem}
        onSuccess={() => {widget.notify(); loadContent(searchTerm)}} />
      <ConfirmDialog
        open={confirmDialogOpen}
        actionUrl="/widgets"
        onSubmit={handleSaveConfirmDialog}
        onClose={() => setConfirmDialogOpen(false)}
        item={currentItem}
        title={t('Remove widget?')}
        content={t('Are you sure you want to remove this widget? All associated data will be removed!')}
      />
      <WidgetTransferDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        currentItem={currentItem}
        onSubmit={() => window.location.reload()}
      />
    </CardContent>
    </Card>
  );
};

WidgetsList.propTypes = {
  configUrl: PropTypes.string
};

export default WidgetsList;
